import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import './Registration.css';

const detectDeviceOS = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'iOS';
  if (ua.includes('android')) return 'Android';
  if (ua.includes('win')) return 'Windows';
  if (ua.includes('mac')) return 'Mac';
  if (ua.includes('linux')) return 'Linux';
  return 'Android';
};

const ETHNICITIES = [
  { val: "south_asian", label: "South Asian" },
  { val: "white", label: "White" },
  { val: "black", label: "Black" },
  { val: "coloured", label: "Coloured" },
  { val: "middle_eastern", label: "Middle Eastern" },
  { val: "north_african", label: "North African" },
  { val: "hispanic", label: "Hispanic" },
  { val: "central_asian", label: "Central Asian" },
  { val: "east_asian", label: "East Asian" },
  { val: "southeast_asian", label: "Southeast Asian" },
  { val: "pacific_islander", label: "Pacific Islander" },
  { val: "maori", label: "Maori" },
  { val: "aboriginal_australian", label: "Aboriginal Australian" },
  { val: "native_american", label: "Native American" },
  { val: "indigenous_south_american", label: "Indigenous South American" },
  { val: "other", label: "Other" }
];

const Registration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_no: '',
    dob: '',
    country: 'IN',
    gender: 'Male',
    ethnicity: 'south_asian',
    device_os: detectDeviceOS(),
    referral_by: '',
    android_version: 'Android 14'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    const userId = sessionStorage.getItem('userId');
    const userName = sessionStorage.getItem('userName');
    if (userId && userName) {
      navigate(`/dashboard/${encodeURIComponent(userName)}`);
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    const { name, email, mobile_no, dob, country, gender, referral_by } = formData;

    if (!name.trim()) {
      setError('Name is required');
      return false;
    }

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('Valid email is required');
      return false;
    }

    if (!mobile_no.trim() || !/^\d{10,15}$/.test(mobile_no)) {
      setError('Valid mobile number is required (10-15 digits)');
      return false;
    }

    if (!dob) {
      setError('Date of birth is required');
      return false;
    }

    if (!country.trim()) {
      setError('Country is required');
      return false;
    }

    if (!gender.trim()) {
      setError('Gender is required');
      return false;
    }

    if (!referral_by.trim()) {
      setError('Referral is required');
      return false;
    }

    if (!consentAccepted) {
      setError('You must accept the consent agreement to register');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        device_os: formData.device_os || detectDeviceOS()
      };
      const response = await apiService.registerUser(payload);
      const userId = response.user_id;
      const userName = response.name;
      sessionStorage.setItem('userId', String(userId));
      sessionStorage.setItem('userName', userName);
      navigate(`/dashboard/${encodeURIComponent(userName)}`);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-container">
      <h2>User Registration</h2>
      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-group">
          <label htmlFor="name">Full Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="mobile_no">Mobile Number:</label>
          <input
            type="tel"
            id="mobile_no"
            name="mobile_no"
            value={formData.mobile_no}
            onChange={handleChange}
            placeholder="Enter your mobile number"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="dob">Date of Birth:</label>
          <input
            type="date"
            id="dob"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="referral_by">Referral by:</label>
          <input
            type="text"
            id="referral_by"
            name="referral_by"
            value={formData.referral_by}
            onChange={handleChange}
            placeholder="Enter referral name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="country">Country:</label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="e.g., IN, US"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="gender">Gender:</label>
          <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="ethnicity">Ethnicity:</label>
          <select id="ethnicity" name="ethnicity" value={formData.ethnicity} onChange={handleChange} required>
            {ETHNICITIES.map(e => (
              <option key={e.val} value={e.val}>{e.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="device_os">Detected Device OS:</label>
          <input
            type="text"
            id="device_os"
            name="device_os"
            value={formData.device_os}
            onChange={handleChange}
            placeholder="e.g., iOS, Android, Windows, Mac"
            required
          />
        </div>

        <div className="form-group consent-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              required
            />
            <span>
              I agree to the <Link to="/consent" target="_blank" rel="noopener noreferrer">Consent Agreement</Link> and permit processing of my photos.
            </span>
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Registering...' : 'Register & Continue'}
        </button>
      </form>
    </div>
  );
};

export default Registration;
